import { ApiClient } from './api'

export interface FiltrosReporte {
  fecha_inicio?: string
  fecha_fin?: string
  tipo_id?: string
  estado?: string
}

export interface OcupacionData {
  periodo: string
  tipo: string
  reservas: number
  ocupacion_pct: number
}

export interface OcupacionResponse {
  datos: OcupacionData[]
  resumen: {
    total_reservas: number
    ocupacion_promedio: number
    periodo: string
    tipos_unicos: number
  }
}

export interface IngresoData {
  tipo: string
  reservas: number
  ingresos_totales: number
  ingresos_promedio: number
}

export interface IngresoResponse {
  datos: IngresoData[]
  resumen: {
    ingresos_totales: number
    ingresos_promedio: number
    cantidad_reservas: number
    ingresos_por_reserva: number
  }
}

export interface EspacioTopData {
  codigo: string
  nombre: string
  tipo: string
  reservas: number
  noches_totales: number
  ingresos: number
}

export interface EspacioTopResponse {
  datos: EspacioTopData[]
}

export interface MetodoPagoData {
  metodo_pago: string
  cantidad: number
  monto_total: number
  porcentaje: number
}

export interface MetodoPagoResponse {
  datos: MetodoPagoData[]
}

// Control Report Types
export interface NodoValidacion {
  tiene_problema: boolean
  mensaje: string
  tipo_problema: string | null
}

export interface NodoDTE {
  id: string
  numero_dte: string
  tipo_documento: string
  monto: number
  status_dte: string
  fecha_emision: string | null
}

export interface NodeoPago {
  id: number
  monto: number
  estado: string
  proveedor_pago: string
  fecha_pago: string | null
  tiene_comprobante: boolean
  dtes: NodoDTE[]
  validacion: NodoValidacion
}

export interface NodoReserva {
  id: string
  codigo: string
  monto_total: number
  estado: string
  estado_pago: string
  fecha_entrada: string
  fecha_salida: string
  cliente_nombre: string
  pagos: NodeoPago[]
  cantidad_pagos: number
  monto_pagado: number
  cantidad_problemas: number  // Total de problemas en pagos de esta reserva
  cantidad_errores: number  // Errores críticos (rojo)
  cantidad_warnings: number  // Warnings (amarillo)
  validacion: NodoValidacion
}

export interface NodoEspacio {
  id: string
  codigo: string
  nombre: string
  reservas: NodoReserva[]
  cantidad_reservas: number
  cantidad_problemas: number
  cantidad_errores: number  // Errores críticos (rojo)
  cantidad_warnings: number  // Warnings (amarillo)
}

export interface NodoTipo {
  id: string
  nombre: string
  espacios: NodoEspacio[]
  cantidad_espacios: number
  cantidad_reservas: number
  cantidad_pagos: number
  cantidad_dtes: number
  cantidad_problemas: number
  cantidad_errores: number  // Errores críticos (rojo)
  cantidad_warnings: number  // Warnings (amarillo)
}

export interface ControlResponse {
  periodo: {
    inicio: string
    fin: string
  }
  empresa_id: number
  tipos: NodoTipo[]
  resumen: {
    total_reservas: number
    total_pagos: number
    total_dtes: number
    problemas_detectados: number
    reservas_sin_pago: number
    pagos_estado_incorrecto: number
    pagos_manuales_sin_adjunto: number
    pagos_manuales_sin_referencia: number
  }
}

class ReportService {
  private apiUrl = '/api/admin-protected/reportes'

  async getOcupacion(filtros: FiltrosReporte): Promise<OcupacionResponse> {
    const response = await ApiClient.post<OcupacionResponse>(`${this.apiUrl}/ocupacion`, filtros)
    // ApiClient.post() already returns response.data, which IS the OcupacionResponse
    const data = response as unknown as OcupacionResponse
    if (!data || !data.datos) throw new Error('No data returned from server')
    return data
  }

  async getIngresos(filtros: FiltrosReporte): Promise<IngresoResponse> {
    const response = await ApiClient.post<IngresoResponse>(`${this.apiUrl}/ingresos`, filtros)
    // ApiClient.post() already returns response.data, which IS the IngresoResponse
    const data = response as unknown as IngresoResponse
    if (!data || !data.datos) throw new Error('No data returned from server')
    return data
  }

  async getEspaciosTop(filtros: FiltrosReporte): Promise<EspacioTopResponse> {
    const response = await ApiClient.post<EspacioTopResponse>(`${this.apiUrl}/espacios-top`, filtros)
    // ApiClient.post() already returns response.data, which IS the EspacioTopResponse
    const data = response as unknown as EspacioTopResponse
    if (!data || !data.datos) throw new Error('No data returned from server')
    return data
  }

  async getMetodosPago(filtros: FiltrosReporte): Promise<MetodoPagoResponse> {
    const response = await ApiClient.post<MetodoPagoResponse>(`${this.apiUrl}/metodos-pago`, filtros)
    // ApiClient.post() already returns response.data, which IS the MetodoPagoResponse
    const data = response as unknown as MetodoPagoResponse
    if (!data || !data.datos) throw new Error('No data returned from server')
    return data
  }

  async getControl(filtros: FiltrosReporte & { mostrar_solo_problemas?: boolean }): Promise<ControlResponse> {
    const response = await ApiClient.post<ControlResponse>(`${this.apiUrl}/control`, filtros)
    // ApiClient.post() already returns response.data, which IS the ControlResponse
    const data = response as unknown as ControlResponse
    if (!data || !data.tipos) throw new Error('No data returned from server')
    return data
  }
}

export default new ReportService()
